import { v5 as uuidv5 } from 'uuid'

/**
 * UUID v5 Service for generating deterministic UUIDs with proper namespaces
 *
 * This service generates UUID v5 identifiers for conversations, messages, and sentences
 * using configured namespaces and input data. UUID v5 ensures deterministic generation
 * based on namespace and input data, which is critical for consistency and validation.
 */
// Fallback namespaces used when env vars are not configured (standard IANA UUIDs)
const FALLBACK_NAMESPACES = {
  conversation: '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
  message: '6ba7b811-9dad-11d1-80b4-00c04fd430c8',
  sentence: '6ba7b812-9dad-11d1-80b4-00c04fd430c8',
}

class UuidService {
  constructor() {
    this.conversationNamespace =
      import.meta.env.VITE_UUID_CONVERSATION_NAMESPACE || FALLBACK_NAMESPACES.conversation
    this.messageNamespace =
      import.meta.env.VITE_UUID_MESSAGE_NAMESPACE || FALLBACK_NAMESPACES.message
    this.sentenceNamespace =
      import.meta.env.VITE_UUID_SENTENCE_NAMESPACE || FALLBACK_NAMESPACES.sentence
  }

  /**
   * Generates a deterministic conversation ID
   *
   * @param {string} userId - The user ID (can be anonymous ID or authenticated user ID)
   * @param {number|Date} timestamp - The conversation start timestamp
   * @returns {string} UUID v5 conversation ID
   * @throws {Error} If required parameters are missing
   *
   * @example
   * const conversationId = uuidService.generateConversationId('user123', Date.now())
   */
  generateConversationId(userId, timestamp) {
    if (!userId) {
      throw new Error('userId is required for conversation ID generation')
    }
    if (!timestamp) {
      throw new Error('timestamp is required for conversation ID generation')
    }

    // Convert timestamp to consistent format
    const timestampValue = timestamp instanceof Date ? timestamp.getTime() : Number(timestamp)
    if (isNaN(timestampValue)) {
      throw new Error('timestamp must be a valid number or Date object')
    }

    // Create deterministic input string
    const input = `conversation:${userId}:${timestampValue}`

    return uuidv5(input, this.conversationNamespace)
  }

  /**
   * Generates a deterministic message ID
   *
   * @param {string} conversationId - The parent conversation ID
   * @param {number|Date} timestamp - The message timestamp
   * @param {string} role - The message role ('user' or 'assistant')
   * @returns {string} UUID v5 message ID
   * @throws {Error} If required parameters are missing or invalid
   *
   * @example
   * const messageId = uuidService.generateMessageId(conversationId, Date.now(), 'user')
   */
  generateMessageId(conversationId, timestamp, role) {
    if (!conversationId) {
      throw new Error('conversationId is required for message ID generation')
    }
    if (!timestamp) {
      throw new Error('timestamp is required for message ID generation')
    }
    if (!role) {
      throw new Error('role is required for message ID generation')
    }

    // Validate role
    if (!['user', 'assistant'].includes(role)) {
      throw new Error('role must be either "user" or "assistant"')
    }

    // Convert timestamp to consistent format
    const timestampValue = timestamp instanceof Date ? timestamp.getTime() : Number(timestamp)
    if (isNaN(timestampValue)) {
      throw new Error('timestamp must be a valid number or Date object')
    }

    // Create deterministic input string
    const input = `message:${conversationId}:${timestampValue}:${role}`

    return uuidv5(input, this.messageNamespace)
  }

  /**
   * Generates a deterministic sentence ID
   *
   * @param {string} messageId - The parent message ID
   * @param {number} position - The sentence position within the message (0-based)
   * @returns {string} UUID v5 sentence ID
   * @throws {Error} If required parameters are missing or invalid
   *
   * @example
   * const sentenceId = uuidService.generateSentenceId(messageId, 0)
   */
  generateSentenceId(messageId, position) {
    if (!messageId) {
      throw new Error('messageId is required for sentence ID generation')
    }
    if (position === undefined || position === null) {
      throw new Error('position is required for sentence ID generation')
    }

    // Validate position
    const positionValue = Number(position)
    if (isNaN(positionValue) || positionValue < 0 || !Number.isInteger(positionValue)) {
      throw new Error('position must be a non-negative integer')
    }

    // Create deterministic input string
    const input = `sentence:${messageId}:${positionValue}`

    return uuidv5(input, this.sentenceNamespace)
  }

  /**
   * Get the configured namespaces (for testing and validation purposes)
   * @returns {Object} The configured namespaces
   */
  getNamespaces() {
    return {
      conversation: this.conversationNamespace,
      message: this.messageNamespace,
      sentence: this.sentenceNamespace,
    }
  }
}

// Create and export a singleton instance
const uuidService = new UuidService()

export default uuidService
